/**
 * 夥伴詳情（PRD §G）— 分頁籤收納四組管理動作。
 *
 * 分頁而非長表單的理由：這四組的風險級距差很大。改級別只是調配額，重產 API Key
 * 卻會讓廠商已上線的串接立刻中斷。放在同一個捲動頁面上，危險操作會被順手滑過去。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { Button } from '../components/Button';
import { Field, ModalShell, inputStyle, useEscapeToClose } from '../components/Modal';
import { PartnerAllocationsTab } from '../components/PartnerAllocationsTab';
import { PartnerSuspensionTab } from '../components/PartnerSuspensionTab';
import { PartnerWebhookTab } from '../components/PartnerWebhookTab';
import { Pill } from '../components/Pill';
import { SecretHandoffModal } from '../components/SecretHandoff';
import {
  getPartner,
  PARTNER_TIER_DEFAULTS,
  regeneratePartnerKey,
  setPartnerTier,
  type Partner,
  type PartnerTier,
  type PartnerWithSecret,
} from '../lib/api-client';
import { formatDateTime, formatQuota, TIER_LABEL } from '../lib/partner-format';

type TabKey = 'basic' | 'webhook' | 'allocations' | 'suspension';

const TABS: ReadonlyArray<{ key: TabKey; label: string }> = [
  { key: 'basic', label: '基本資料與金鑰' },
  { key: 'webhook', label: 'Webhook' },
  { key: 'allocations', label: '班次配額' },
  { key: 'suspension', label: '停權管理' },
];

export function PartnerDetailPage(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const [tab, setTab] = useState<TabKey>('basic');

  const {
    data: partner,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['partners.detail', id],
    queryFn: () => getPartner(id),
    enabled: id.length > 0,
  });

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">
          {partner ? partner.display_name : '夥伴詳情'}
          {partner && (
            <code
              style={{ fontFamily: 'monospace', fontSize: 13, color: '#6b7280', marginLeft: 10 }}
            >
              {partner.partner_code}
            </code>
          )}
        </h1>
        <div className="page-head__actions">
          <Link to="/admin/partners">← 回夥伴清單</Link>
        </div>
      </div>

      {isLoading && <div>載入中…</div>}
      {error && <div className="error-banner">{(error as Error).message}</div>}

      {partner && (
        <>
          <div
            style={{
              display: 'flex',
              gap: 4,
              borderBottom: '1px solid #e5e7eb',
              margin: '0 0 18px',
            }}
          >
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                style={{
                  padding: '8px 14px',
                  fontSize: 13,
                  fontWeight: tab === t.key ? 700 : 400,
                  color: tab === t.key ? '#0b5c8a' : '#6b7280',
                  background: 'none',
                  border: 'none',
                  borderBottom: `2px solid ${tab === t.key ? '#0b5c8a' : 'transparent'}`,
                  marginBottom: -1,
                  cursor: 'pointer',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          {tab === 'basic' && <BasicTab partner={partner} />}
          {tab === 'webhook' && <PartnerWebhookTab partner={partner} />}
          {tab === 'allocations' && <PartnerAllocationsTab partnerId={partner.id} />}
          {tab === 'suspension' && <PartnerSuspensionTab partner={partner} />}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- 基本資料

function BasicTab({ partner }: { partner: Partner }): JSX.Element {
  const [changingTier, setChangingTier] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(false);
  const [issued, setIssued] = useState<PartnerWithSecret | null>(null);
  const queryClient = useQueryClient();

  const regen = useMutation({
    mutationFn: () => regeneratePartnerKey(partner.id),
    onSuccess: (p) => {
      void queryClient.invalidateQueries({ queryKey: ['partners.list'] });
      setConfirmRegen(false);
      setIssued(p);
    },
  });

  return (
    <div>
      <div className="table-card" style={{ marginBottom: 20 }}>
        <table>
          <tbody>
            <Row label="夥伴代碼">
              <code style={{ fontFamily: 'monospace' }}>{partner.partner_code}</code>
              <span style={{ fontSize: 11, color: '#6b7280', marginLeft: 8 }}>建立後不可修改</span>
            </Row>
            <Row label="顯示名稱">{partner.display_name}</Row>
            <Row label="級別">
              <Pill tone="brand">{TIER_LABEL[partner.tier] ?? partner.tier}</Pill>
              <Button
                variant="secondary"
                onClick={() => setChangingTier(true)}
                style={{ marginLeft: 12 }}
              >
                變更級別
              </Button>
            </Row>
            <Row label="配額">
              每分鐘 {formatQuota(partner.quota_per_minute)} 次、每月{' '}
              {formatQuota(partner.quota_per_month)} 次、同時保留 {partner.max_active_holds} 筆、
              保留時效 {partner.hold_ttl_seconds / 60} 分鐘
            </Row>
            <Row label="OAuth Client ID">
              <code style={{ fontFamily: 'monospace', fontSize: 12 }}>
                {partner.oauth_client_id}
              </code>
            </Row>
            <Row label="狀態">
              {partner.is_active ? (
                <Pill tone="success">啟用中</Pill>
              ) : (
                <Pill tone="danger">已停權</Pill>
              )}
            </Row>
            <Row label="建立 / 更新">
              {formatDateTime(partner.created_at)} / {formatDateTime(partner.updated_at)}
            </Row>
          </tbody>
        </table>
      </div>

      <h2 style={{ fontSize: 15, margin: '0 0 6px' }}>API Key</h2>
      <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 10px' }}>
        系統只保存雜湊值，無法查看現有金鑰。廠商遺失時只能重新產生 ——
        <strong>舊金鑰會立即失效</strong>，其已上線的串接會在換上新金鑰前全部中斷。
      </p>
      <Button variant="danger" onClick={() => setConfirmRegen(true)}>
        重新產生 API Key
      </Button>

      {changingTier && <ChangeTierModal partner={partner} onClose={() => setChangingTier(false)} />}

      {confirmRegen && (
        <ConfirmRegenModal
          partner={partner}
          pending={regen.isPending}
          error={regen.error as Error | null}
          onCancel={() => setConfirmRegen(false)}
          onConfirm={() => regen.mutate()}
        />
      )}

      {issued && (
        <SecretHandoffModal
          title="API Key 已重新產生"
          subtitle={
            <>
              <strong>{issued.display_name}</strong>（
              <code style={{ fontFamily: 'monospace' }}>{issued.partner_code}</code>）
            </>
          }
          warning={
            <>
              <strong>舊金鑰已立即失效。</strong>
              請盡快將新金鑰交付廠商 —— 在他們換上之前，所有 API 呼叫都會回 401。
              此金鑰同樣只顯示這一次。
            </>
          }
          secretLabel="新 API Key"
          secret={issued.api_key}
          onAcknowledge={() => setIssued(null)}
        />
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <tr>
      <td style={{ width: 160, color: '#6b7280', fontSize: 13 }}>{label}</td>
      <td>{children}</td>
    </tr>
  );
}

// ---------------------------------------------------------------- 變更級別

function ChangeTierModal({
  partner,
  onClose,
}: {
  partner: Partner;
  onClose: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [tier, setTier] = useState<PartnerTier>(partner.tier);

  const mutation = useMutation({
    mutationFn: () => setPartnerTier(partner.id, tier),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['partners.detail', partner.id] });
      void queryClient.invalidateQueries({ queryKey: ['partners.list'] });
      onClose();
    },
  });

  useEscapeToClose(onClose, !mutation.isPending);
  const q = PARTNER_TIER_DEFAULTS[tier];

  return (
    <ModalShell onBackdropClick={mutation.isPending ? undefined : onClose} width={480}>
      <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>變更級別</h2>
      <p style={{ margin: '0 0 16px', fontSize: 12, color: '#6b7280' }}>
        變更級別會<strong>一併重設配額</strong>為該級別的預設值，先前的個別調整會被覆蓋。
      </p>

      <Field label="級別">
        <select
          value={tier}
          onChange={(e) => setTier(e.target.value as PartnerTier)}
          style={inputStyle}
        >
          {(Object.keys(TIER_LABEL) as PartnerTier[]).map((t) => (
            <option key={t} value={t}>
              {TIER_LABEL[t]}
            </option>
          ))}
        </select>
      </Field>

      <div
        style={{
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          padding: '8px 12px',
          fontSize: 12,
          marginBottom: 16,
        }}
      >
        套用後：每分鐘 {q.quotaPerMinute.toLocaleString('zh-TW')} 次、每月{' '}
        {q.quotaPerMonth.toLocaleString('zh-TW')} 次、同時保留 {q.maxActiveHolds} 筆、 保留時效{' '}
        {q.holdTtlSeconds / 60} 分鐘
      </div>

      {mutation.isError && (
        <div className="error-banner" style={{ marginBottom: 12 }}>
          {(mutation.error as Error).message}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
          取消
        </Button>
        <Button
          variant="primary"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending || tier === partner.tier}
        >
          {mutation.isPending ? '套用中…' : '套用'}
        </Button>
      </div>
    </ModalShell>
  );
}

// ---------------------------------------------------------------- 重產金鑰確認

function ConfirmRegenModal({
  partner,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  partner: Partner;
  pending: boolean;
  error: Error | null;
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  // 打字確認：這個動作會直接中斷廠商營運，不該只隔一個「確定」按鈕
  const [typed, setTyped] = useState('');
  useEscapeToClose(onCancel, !pending);

  return (
    <ModalShell onBackdropClick={pending ? undefined : onCancel} width={520}>
      <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>重新產生 API Key？</h2>
      <div
        style={{
          background: '#fef2f2',
          border: '1px solid #fca5a5',
          borderLeft: '4px solid #dc2626',
          borderRadius: 6,
          padding: '10px 14px',
          fontSize: 13,
          margin: '12px 0 16px',
        }}
      >
        <strong>{partner.display_name}</strong> 目前的 API Key 會<strong>立即失效</strong>。
        在對方換上新金鑰之前，其所有 API 呼叫都會回 401 —— 包含線上售票。
        請先與廠商約好切換時間再執行。
      </div>

      <Field label={`請輸入夥伴代碼 ${partner.partner_code} 以確認`}>
        <input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={partner.partner_code}
          style={{ ...inputStyle, fontFamily: 'monospace' }}
          autoFocus
        />
      </Field>

      {error && (
        <div className="error-banner" style={{ marginBottom: 12 }}>
          {error.message}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button variant="secondary" onClick={onCancel} disabled={pending}>
          取消
        </Button>
        <Button
          variant="danger"
          onClick={onConfirm}
          disabled={pending || typed.trim() !== partner.partner_code}
        >
          {pending ? '產生中…' : '確認重新產生'}
        </Button>
      </div>
    </ModalShell>
  );
}
